(() => {
  if (window.pointsNavigationReady) return;
  window.pointsNavigationReady = true;
  // Delegate so cart drawer HTML replaced after a cart update is covered too.
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-points-cart-target]');
    if (!link) return;
    const target = new URL(link.dataset.pointsCartTarget, location.origin);
    if (target.origin !== location.origin) return;
    if (window.authAPI?.getAccessToken() && window.authAPI?.getRefreshToken()) return;
    event.preventDefault();
    // The login entry guard accepts an access token alone. Clear a partial
    // session so it cannot skip login without restoring both credentials.
    window.authAPI?.clearAllTokens();
    try { sessionStorage.setItem('points-login-return', target.pathname + target.search + target.hash); } catch (_) { /* Login remains available. */ }
    location.href = target.pathname.replace(/\/pages\/points-coupons\/?$/, '/pages/login');
  }, true);
  // Keep the selected coupon across page visits in this tab. Shopify owns
  // discount validity; storing a code is not proof that it was accepted.
  const discountKey = 'points-selected-discount';
  let selectedDiscount = '';
  try { selectedDiscount = sessionStorage.getItem(discountKey) || ''; } catch (_) { /* Storage may be disabled. */ }
  const discountParams = new URLSearchParams(location.search);
  // Shopify shareable discount links leave a cookie after their redirect.
  // Prefer it to a stale same-tab selection when the URL has no explicit code.
  const discountCookie = document.cookie.split(';').find(item => item.trim().startsWith('discount_code='));
  if (!discountParams.has('discount') && discountCookie) {
    try { selectedDiscount = decodeURIComponent(discountCookie.trim().slice('discount_code='.length)).trim(); }
    catch (_) { selectedDiscount = ''; }
  }
  if (discountParams.has('discount') || discountCookie) {
    if (discountParams.has('discount')) selectedDiscount = (discountParams.get('discount') || '').trim();
    try {
      if (selectedDiscount) sessionStorage.setItem(discountKey, selectedDiscount);
      else sessionStorage.removeItem(discountKey);
    } catch (_) { /* The current page can still apply its code. */ }
  }
  // Replace the actual cart's selected code too. The /discount endpoint only
  // updates the cookie and can leave an existing cart on its previous code.
  let discountApplication;
  function applyDiscount() {
    if (!selectedDiscount) return Promise.resolve();
    if (!discountApplication) {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL('/discount/' + encodeURIComponent(selectedDiscount), location.origin);
      url.searchParams.set('redirect', root.replace(/\/$/, '') + '/cart');
      const updateUrl = root.replace(/\/$/, '') + '/cart/update.js';
      discountApplication = fetch(updateUrl, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: selectedDiscount }), signal: AbortSignal.timeout(10000)
      })
        .then(async response => {
          if (!response.ok) throw new Error('Cart discount update failed');
          const cart = await response.json();
          if (!Array.isArray(cart.items)) throw new Error('Invalid cart discount response');
          document.dispatchEvent(new CustomEvent('cart:discount-updated', { detail: cart }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'points-discount', cartData: cart });
          }
          return fetch(url.href, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
        })
        .then(response => { if (!response.ok) throw new Error('Discount endpoint failed'); })
        .catch(() => {
          console.warn('[Points discount] Shopify application could not be confirmed; code will be included in standard checkout.');
          discountApplication = null;
        });
    }
    return discountApplication;
  }
  // A product view only previews the code in an isolated Storefront cart.
  // Apply to the real cart when the buyer opens it or continues to checkout.
  const isProductPage = /\/products\/[^/]+\/?$/.test(location.pathname);
  if (selectedDiscount && !isProductPage) applyDiscount();
  document.addEventListener('cart:opened', () => { if (selectedDiscount) applyDiscount(); });
  const resumingCheckout = new WeakSet();
  const pendingCheckout = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !selectedDiscount || resumingCheckout.has(form)) return;
    const action = new URL(form.action, location.origin);
    if (action.origin !== location.origin || !/\/(?:cart|checkout)\/?$/.test(action.pathname)) return;
    if (!/\/checkout\/?$/.test(action.pathname) && event.submitter?.name !== 'checkout') return;
    if (pendingCheckout.has(form)) { event.preventDefault(); return; }
    // Preserve the native cart submission, including quantities, notes and
    // the checkout submitter. Do not overwrite an explicit discount field.
    let input = form.querySelector('[name="discount"]');
    if (input && input.value && input.value !== selectedDiscount) return;
    if (!input) {
      input = document.createElement('input'); input.type = 'hidden';
      input.name = 'discount'; form.append(input);
    }
    input.value = selectedDiscount;
    event.preventDefault();
    pendingCheckout.add(form);
    const submitter = event.submitter;
    await applyDiscount();
    // A cached application may resolve during the original submit dispatch.
    // Resume in a new task so the browser does not suppress reentrant submit.
    await new Promise(resolve => setTimeout(resolve, 0));
    pendingCheckout.delete(form);
    if (!form.isConnected) return;
    resumingCheckout.add(form);
    try { form.requestSubmit(submitter || undefined); }
    finally { resumingCheckout.delete(form); }
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || !selectedDiscount) return;
    const url = new URL(link.href, location.origin);
    if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname) && !url.searchParams.has('discount')) {
      url.searchParams.set('discount', selectedDiscount); link.href = url.href;
    }
  }, true);
  window.consumePointsLoginReturn = function () {
    try {
      const target = sessionStorage.getItem('points-login-return');
      if (!target) return '';
      sessionStorage.removeItem('points-login-return');
      const url = new URL(target, location.origin);
      if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/(rewards|rewards-desktop|points-activity|points-redemption|points-coupons)\/?$/.test(url.pathname)) return '';
      url.pathname = url.pathname.replace(/\/pages\/rewards-desktop\/?$/, '/pages/points-activity');
      return url.pathname + url.search + url.hash;
    } catch (_) { return ''; }
  };
  function resumePointsAfterLogin() {
    if (!/\/pages\/(my-account|mobile-menu)\/?$/.test(location.pathname) || !window.authAPI?.getAccessToken()) return;
    const target = window.consumePointsLoginReturn();
    if (target) location.replace(target);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumePointsAfterLogin);
  else resumePointsAfterLogin();
  function rememberCart() {
    try { sessionStorage.setItem('points-return-to-cart', '1'); } catch (_) { /* private browsing */ }
  }
  if (/\/cart\/?$/.test(location.pathname) || new URLSearchParams(location.search).get('from') === 'cart') rememberCart();
  document.addEventListener('cart:open', rememberCart);
  document.addEventListener('keydown', (event) => {
    if ((event.code === 'Space' || event.key === 'Enter') && event.target.closest('#cart-icon-bubble, .component-navbar__cart-link')) rememberCart();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest('.points-links--cart a, #cart-icon-bubble, .component-navbar__cart-link')) rememberCart();
  });
  if (new URLSearchParams(location.search).get('points_cart') === '1') {
    const open = () => {
      if (document.querySelector('cart-drawer')) customElements.whenDefined('cart-drawer').then(() => document.dispatchEvent(new CustomEvent('cart:open')));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', open); else open();
  }
})();
